import { useEffect, useRef, type MutableRefObject } from 'react';
import { getPerformanceMark } from '.';
import { useHeightObserver } from './useHeightObserver';
import { usePerformanceAnalytics } from './usePerformanceAnalytics';
import { PERFORMANCE_ANALYTICS_INTERVAL } from './PerformanceAnalyticsProvider';

import type { ViewportDimensions } from '../renderer/forge-ui-renderer/types';

interface Props {
	isEnabled: boolean;
	viewportDimensions?: ViewportDimensions;
}

export const useResizeAnalytics = ({
	isEnabled,
	viewportDimensions,
}: Props): {
	containerRef: MutableRefObject<HTMLDivElement | null>;
} => {
	const { captureEvent } = usePerformanceAnalytics();
	const { containerRef, height } = useHeightObserver();
	const isTracking = useRef(true);

	useEffect(() => {
		// Stop tracking after 10 seconds
		const timeoutId = setTimeout(() => {
			isTracking.current = false;
		}, PERFORMANCE_ANALYTICS_INTERVAL);

		return () => clearTimeout(timeoutId);
	}, []);

	useEffect(() => {
		if (height !== null && isEnabled && isTracking.current) {
			isTracking.current = true;
			captureEvent({
				type: 'resized',
				height,
				measuredHeight: viewportDimensions?.measuredHeight,
				viewportHeight: viewportDimensions?.viewportHeight,
				elapsed: getPerformanceMark(),
			});
		}
	}, [height, isEnabled, captureEvent, viewportDimensions]);

	return { containerRef };
};
